import { useBusinessOptions } from '@/services/businessOptions'
import { affectsWorkspace, latestRequest } from '@/services/workspaceRefresh'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Alert, Button, Form, InputGroup } from 'react-bootstrap'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCirclePlay, faCircleStop, faFileInvoice, faMagnifyingGlass, faNoteSticky, faPlus } from '@fortawesome/free-solid-svg-icons'
import { AdminPageHero } from '@/components/common/AdminPageHero'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ResultsPager } from '@/components/common/ResultsPager'
import { useCrmHub } from '@/context/SignalRContext'
import { useAuth } from '@/context/AuthContext'
import { adminRoles, editorRoles } from '@/helpers/roles'
import { readError } from '@/services/apiClient'
import { loadAssignableUsers } from '@/services/actionService'
import { StopReasonDialog } from '@/components/customers/StopReasonDialog'
import { searchCustomerPage, type CustomerResult } from '@/services/customerService'
import type { Contact, CrmUser } from '@/models/api'

const pageSize = 20

interface CustomerSearchMemory {
  search: string
  ownerId: string
  includeDeleted: boolean
  page: number
  rows: CustomerResult[]
  total: number
}

function customerSearchKey(businessId: string) {
  return `crm-customer-search:${businessId}`
}

function readCustomerSearch(businessId: string | null | undefined): CustomerSearchMemory | null {
  if (!businessId) return null
  try {
    const raw = sessionStorage.getItem(customerSearchKey(businessId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CustomerSearchMemory>
    if (!Array.isArray(parsed.rows) || parsed.rows.some((row) => !row || typeof row.id !== 'string')) return null
    const page = typeof parsed.page === 'number' && parsed.page >= 1 ? Math.floor(parsed.page) : 1
    const maxPage = Math.max(1, Math.ceil((parsed.total ?? parsed.rows.length) / pageSize))
    return {
      search: typeof parsed.search === 'string' ? parsed.search : '',
      ownerId: typeof parsed.ownerId === 'string' ? parsed.ownerId : '',
      includeDeleted: parsed.includeDeleted === true,
      page: Math.min(page, maxPage),
      rows: parsed.rows,
      total: parsed.total ?? parsed.rows.length,
    }
  } catch {
    return null
  }
}

function initialSearch(businessId: string | null | undefined, urlQuery: string) {
  const saved = readCustomerSearch(businessId)
  if (!saved) return null
  if (urlQuery && (saved.search !== urlQuery || saved.ownerId)) return null
  return saved
}

export function CustomersPage() {
  const { session } = useAuth()
  const { lastEvent } = useCrmHub()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const canEdit = !!session?.role && editorRoles.includes(session.role)
  const canStop = !!session?.role && adminRoles.includes(session.role)
  const urlQuery = params.get('q')?.trim() ?? ''
  const remembered = initialSearch(session?.businessId, urlQuery)
  const [search, setSearch] = useState(remembered?.search ?? urlQuery)
  const [ownerId, setOwnerId] = useState(remembered?.ownerId ?? '')
  const [includeDeleted, setIncludeDeleted] = useState(remembered?.includeDeleted ?? false)
  const [users, setUsers] = useState<CrmUser[]>([])
  const [rows, setRows] = useState<CustomerResult[] | null>(remembered?.rows ?? null)
  const [total, setTotal] = useState(remembered?.total ?? 0)
  const [page, setPage] = useState(remembered?.page ?? 1)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [stopTarget, setStopTarget] = useState<CustomerResult | null>(null)
  const request = useRef(latestRequest())
  const searching = useRef(false)
  useEffect(() => () => { request.current() }, [])
  const criteriaRef = useRef({
    search: remembered?.search ?? urlQuery,
    ownerId: remembered?.ownerId ?? '',
    includeDeleted: remembered?.includeDeleted ?? false,
  })

  function remember(pageNumber: number, nextRows: CustomerResult[], nextTotal: number) {
    if (!session?.businessId) return
    try {
      const payload: CustomerSearchMemory = { ...criteriaRef.current, page: pageNumber, rows: nextRows, total: nextTotal }
      sessionStorage.setItem(customerSearchKey(session.businessId), JSON.stringify(payload))
    } catch {
      // A full result set can exceed session storage. The list still stays on this visit.
    }
  }

  function forget() {
    if (!session?.businessId) return
    sessionStorage.removeItem(customerSearchKey(session.businessId))
  }

  useEffect(() => {
    loadAssignableUsers().then(setUsers).catch(() => setUsers([]))
  }, [session?.businessId])

  useEffect(() => {
    const query = params.get('q')?.trim()
    if (!query) return
    const saved = readCustomerSearch(session?.businessId)
    if (saved && saved.search === query && !saved.ownerId) return
    void runSearch({ search: query, includeDeleted: false })
  }, [params, session?.businessId])

  useEffect(() => {
    if (!affectsWorkspace(lastEvent, 'customers') || !rows) return
    const criteria = criteriaRef.current
    void runSearch({ search: criteria.search, ownerUserId: criteria.ownerId, includeDeleted: criteria.includeDeleted }, page, true)
  }, [lastEvent])

  async function runSearch(query: { search?: string; ownerUserId?: string; includeDeleted: boolean }, nextPage = 1, background = false) {
    if (background && searching.current) return
    const current = request.current()
    searching.current = !background
    if (!background) setBusy(true)
    setError(null)
    try {
      const result = await searchCustomerPage(query, nextPage, pageSize)
      if (!current()) return
      setPage(nextPage)
      const lastPage = Math.max(1, Math.ceil(result.total / pageSize))
      if (nextPage > lastPage) { await runSearch(query, lastPage, background); return }
      const results = result.rows
      setTotal(result.total)
      criteriaRef.current = {
        search: query.search ?? '',
        ownerId: query.ownerUserId ?? '',
        includeDeleted: query.includeDeleted,
      }
      setRows(results)
      remember(nextPage, results, result.total)
    } catch (caught) {
      if (current()) setError(readError(caught))
    } finally {
      if (current()) { searching.current = false; setBusy(false) }
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    setOwnerId('')
    void runSearch({ search, includeDeleted })
  }

  function onOwner(next: string) {
    setOwnerId(next)
    setSearch('')
    if (!next) {
      setRows(null)
      setPage(1)
      forget()
      return
    }
    void runSearch({ ownerUserId: next, includeDeleted })
  }

  function onPage(next: number) {
    const criteria = criteriaRef.current
    void runSearch({ search: criteria.search, ownerUserId: criteria.ownerId, includeDeleted: criteria.includeDeleted }, next)
  }

  const visible = rows ?? []

  return (
    <>
      <AdminPageHero title="Customers" summary="Find an account by name, address, postcode or contact.">
        {canStop && <Button type="button" variant="outline-primary" onClick={() => navigate("/imports?type=customers")}>Import customers</Button>}
      </AdminPageHero>
      <section className="admin-panel mb-3">
        <Form onSubmit={onSubmit}>
          <div className="customer-search-form mb-3">
            <InputGroup className="customer-search-bar">
              <Form.Control
                value={search}
                placeholder="customer/address/contact name..."
                aria-label="Search customers"
                onChange={(event) => setSearch(event.target.value)}
              />
              <Button type="submit" disabled={busy} title="Search customers">
                <FontAwesomeIcon icon={faMagnifyingGlass} /> search
              </Button>
            </InputGroup>
            {canEdit && (
              <Button variant="success" title="Add a new customer" onClick={() => navigate('/customers/new')}>
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            )}
          </div>
          <Form.Select className="mb-3" aria-label="Select a user" value={ownerId} onChange={(event) => onOwner(event.target.value)} style={{ maxWidth: 420 }}>
            <option value="">select a user...</option>
            {users.map((user) => <option key={user.crmAccountUserId} value={user.crmAccountUserId}>{user.fullName || user.emailAddress}</option>)}
          </Form.Select>
          <Form.Check
            type="switch"
            id="includeDeleted"
            label="Include Deleted"
            checked={includeDeleted}
            onChange={(event) => {
              const next = event.target.checked
              setIncludeDeleted(next)
              if (rows) void runSearch(ownerId ? { ownerUserId: ownerId, includeDeleted: next } : { search, includeDeleted: next })
            }}
          />
        </Form>
      </section>
      {error && <Alert variant="danger">{error}</Alert>}
      {busy && <LoadingSpinner text="Searching" size="3x" />}
      {!busy && rows && rows.length === 0 && <Alert variant="info">No matching customers found, or the selected user does not own any customers.</Alert>}
      {!busy && rows && rows.length > 0 && (
        <>
          <ResultsPager page={page} pageSize={pageSize} total={total} onPage={onPage} />
          <div className="customer-results">
            {visible.map((row) => (
              <CustomerResultCard key={row.id} row={row} canEdit={canEdit} canStop={canStop} onStop={() => setStopTarget(row)} />
            ))}
          </div>
          <ResultsPager page={page} pageSize={pageSize} total={total} onPage={onPage} />
        </>
      )}
      {stopTarget && (
        <StopReasonDialog
          customerId={stopTarget.id}
          customerName={stopTarget.name}
          placingOnStop={!stopTarget.onStop}
          onClose={() => setStopTarget(null)}
          onDone={() => {
            const target = stopTarget
            const next = rows?.map((item) => item.id === target.id ? { ...item, onStop: !target.onStop } : item) ?? null
            setRows(next)
            if (next) remember(page, next, total)
            setStopTarget(null)
          }}
        />
      )}
    </>
  )
}

function CustomerResultCard({
  row,
  canEdit,
  canStop,
  onStop,
}: {
  row: CustomerResult
  canEdit: boolean
  canStop: boolean
  onStop: () => void
}) {
  const { options: businessOptions } = useBusinessOptions()
  const contact = defaultContact(row.contacts)
  const phone = contact?.defaultOrFirstContactNumber || row.contactNumber
  const address = [row.address1, row.address2, row.address3, row.city, row.county].filter(Boolean).join(', ')
  return (
    <article className={`customer-result ${row.onStop ? 'customer-result--stop' : ''}`}>
      <div className="customer-result__top">
        <div>
          <Link className="customer-result__name" to={`/customers/${row.id}`} title="View customer">{row.name}</Link>
          {(row.onStop || row.deleted) && (
            <div className="customer-result__flags">
              {row.onStop && <span className="customer-result__flag customer-result__flag--stop">On stop</span>}
              {row.deleted && <span className="customer-result__flag customer-result__flag--deleted">Deleted</span>}
            </div>
          )}
        </div>
        <div className="customer-result__actions">
          <Link className="btn btn-sm btn-outline-primary" to={`/customers/${row.id}`} title="View details"><FontAwesomeIcon icon={faMagnifyingGlass} /></Link>
          {canEdit && (
            <>
              <Link className="btn btn-sm btn-outline-primary" to={`/orders/new/${row.id}`} title="New submission"><FontAwesomeIcon icon={faFileInvoice} /></Link>
              <Link className="btn btn-sm btn-outline-primary" to={`/actions/new/${row.id}`} title="Add action"><FontAwesomeIcon icon={faPlus} /></Link>
              <Link className="btn btn-sm btn-outline-primary" to={`/notes/new/${row.id}`} title="Add note"><FontAwesomeIcon icon={faNoteSticky} /></Link>
              {canStop && (
                <Button size="sm" variant={row.onStop ? 'outline-success' : 'outline-danger'} title={row.onStop ? 'Remove from stop' : 'Place on stop'} onClick={onStop}>
                  <FontAwesomeIcon icon={row.onStop ? faCirclePlay : faCircleStop} />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      <div className="customer-result__grid">
        <Field label="Account" value={row.accountNumber} />
        <Field label="Discount" value={row.discount} />
        <Field label="Type" value={row.customerType} />
        <Field label="Postcode" value={row.postcode} />
        {businessOptions.useGridReferences && <Field label="Grid ref" value={row.gridRef} />}
        <Field label="Address" value={address} wide />
        <Field label="Contact" value={contact?.fullname || row.name} />
        <Field label="Number" value={phone} href={phone ? `tel:${phone}` : undefined} />
        <Field label="Category" value={row.category} />
        <Field label="Owner" value={row.accountOwnerName} />
      </div>
    </article>
  )
}

function Field({ label, value, href, wide = false }: { label: string; value?: string | null; href?: string; wide?: boolean }) {
  return (
    <div className={wide ? 'customer-result__wide' : undefined}>
      <span>{label}</span>
      {value ? href ? <a href={href}>{value}</a> : value : <em>Not set</em>}
    </div>
  )
}

function defaultContact(contacts: Contact[]) {
  return contacts.find((contact) => contact.defaultContact) ?? contacts[0]
}
